import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { employeeApi, departmentApi, positionApi } from '../services/api';

interface Employee {
  id: number;
  employee_no: string;
  real_name: string;
  phone: string;
  email: string;
  position: string;
  department: string;
  department_id?: number;
  position_id?: number;
  dept?: { id: number; name: string };
  pos?: { id: number; name: string };
  entry_date: string;
  status: number;
  base_salary?: number;
  salary_day?: number;
}

const Employees: React.FC = () => {
  const [data, setData] = useState<Employee[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [form] = Form.useForm();
  const [deptOptions, setDeptOptions] = useState<{ id: number; name: string }[]>([]);
  const [posOptions, setPosOptions] = useState<{ id: number; name: string; department_id: number }[]>([]);
  const [filteredPosOptions, setFilteredPosOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetchEmployees();
    loadOptions();
  }, []);

  const fetchEmployees = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res: any = await employeeApi.getEmployees({ page, page_size: pageSize });
      if (res.code === 200) {
        setData(res.data.list || []);
        setPagination({ current: page, pageSize, total: res.data.total || 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [deptRes, posRes] = await Promise.all([
        departmentApi.getOptions(),
        positionApi.getOptions()
      ]);
      if (deptRes.code === 200) setDeptOptions(deptRes.data || []);
      if (posRes.code === 200) setPosOptions(posRes.data || []);
    } catch (e) { console.error(e); }
  };

  const handleDeptChange = (deptId: number) => {
    if (deptId) {
      setFilteredPosOptions(posOptions.filter(p => p.department_id === deptId));
    } else {
      setFilteredPosOptions(posOptions);
    }
    form.setFieldsValue({ position_id: undefined });
  };

  const handleAdd = () => {
    setEditingEmployee(null);
    form.resetFields();
    setFilteredPosOptions(posOptions);
    setModalOpen(true);
  };

  const handleEdit = (record: Employee) => {
    setEditingEmployee(record);
    if (record.department_id) {
      setFilteredPosOptions(posOptions.filter(p => p.department_id === record.department_id));
    } else {
      setFilteredPosOptions(posOptions);
    }
    form.setFieldsValue({
      ...record,
      department_id: record.department_id || record.dept?.id,
      position_id: record.position_id || record.pos?.id,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const res: any = await employeeApi.deleteEmployee(id);
      if (res.code === 200) {
        message.success('删除成功');
        fetchEmployees(pagination.current, pagination.pageSize);
      }
    } catch { message.error('删除失败'); }
  };

  const handleResetPassword = async (id: number) => {
    try {
      const res: any = await employeeApi.resetPassword(id, '123456');
      if (res.code === 200) message.success('密码已重置为: 123456');
    } catch { message.error('重置失败'); }
  };

  const onFinish = async (values: any) => {
    try {
      let res: any;
      if (editingEmployee) {
        res = await employeeApi.updateEmployee(editingEmployee.id, values);
      } else {
        res = await employeeApi.createEmployee(values);
      }
      if (res.code === 200) {
        message.success(editingEmployee ? '修改成功' : '添加成功');
        setModalOpen(false);
        fetchEmployees(pagination.current, pagination.pageSize);
      }
    } catch { message.error('操作失败'); }
  };

  const columns: ColumnsType<Employee> = [
    { title: '工号', dataIndex: 'employee_no', width: 100 },
    { title: '姓名', dataIndex: 'real_name', width: 120 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '部门', dataIndex: 'department', width: 100 },
    { title: '职位', dataIndex: 'position', width: 100 },
    { title: '入职日期', dataIndex: 'entry_date', width: 120 },
    { title: '月薪', dataIndex: 'base_salary', width: 100, render: v => v ? `¥${v}` : '-' },
    { title: '发薪日', dataIndex: 'salary_day', width: 80, render: v => v ? `${v}号` : '-' },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v: number) => v === 1 ? '在职' : '离职'
    },
    {
      title: '操作', width: 200, fixed: 'right',
      render: (_: any, record: Employee) => (
        <Space>
          <Button size="small" type="link" onClick={() => handleEdit(record)}>编辑</Button>
          <Button size="small" type="link" onClick={() => handleResetPassword(record.id)}>重置密码</Button>
          <Button size="small" type="link" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2>员工管理</h2>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAdd}>新增员工</Button>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1000 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total: number) => `共 ${total} 条`,
          onChange: (page, pageSize) => fetchEmployees(page, pageSize)
        }}
      />
      <Modal
        title={editingEmployee ? '编辑员工' : '新增员工'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} onFinish={onFinish}>
          <Form.Item name="employee_no" label="工号">
            <Input placeholder="员工编号（留空自动生成）" />
          </Form.Item>
          {!editingEmployee && (
            <>
              <Form.Item name="phone" label="手机号（登录账号）" rules={[{ required: true, message: '请输入手机号' }]}>
                <Input placeholder="手机号" />
              </Form.Item>
              <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
                <Input.Password placeholder="初始密码" />
              </Form.Item>
            </>
          )}
          <Form.Item name="real_name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="真实姓名" />
          </Form.Item>
          <Form.Item name="department_id" label="部门">
            <Select placeholder="请选择部门" allowClear onChange={handleDeptChange} showSearch optionFilterProp="children">
              {deptOptions.map(d => <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="position_id" label="职位">
            <Select placeholder="请先选择部门" allowClear showSearch optionFilterProp="children">
              {filteredPosOptions.map(p => <Select.Option key={p.id} value={p.id}>{p.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="base_salary" label="月薪">
            <Input type="number" placeholder="基本工资" addonAfter="元/月" />
          </Form.Item>
          <Form.Item name="salary_day" label="发薪日" initialValue={15}>
            <Input type="number" placeholder="每月几号发薪" addonAfter="号" />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input placeholder="邮箱" />
          </Form.Item>
          <Form.Item name="entry_date" label="入职日期">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue={1}>
            <Select>
              <Select.Option value={1}>在职</Select.Option>
              <Select.Option value={0}>离职</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Employees;